import React, { useEffect } from 'react';

import { displayAxiosError } from '@apis/next/errors';
import { FormAlert, FormAlertProps } from '@components/Forms/FormAlert';
import { SpinnerModal } from '@components/SpinnerModal';
import { UseConfirmDialogOptions, useConfirmDialog } from '@hooks/useActionableDialog';
import type { UseMutationResult } from '@tanstack/react-query';
import { FormError } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';

const strings = createStrings({
  ja: {
    unknownError: 'エラーが発生しました。',
  },
});

export interface FormUtilsOption {
  mutations: UseMutationResult<any, any, any, any>[];
  formAlertProps?: Partial<FormAlertProps>;
  confirmDialogProps?: UseConfirmDialogOptions;
}

export const useFormUtils = (options: FormUtilsOption) => {
  const { mutations, formAlertProps, confirmDialogProps } = options;

  const { renderDialog, confirm } = useConfirmDialog(confirmDialogProps);

  const renderAlerts = () =>
    mutations.map((mutation, i) => (
      <FormAlert key={i} mutation={mutation} gutter {...formAlertProps} />
    ));

  const renderAlertDialog = () => renderDialog();

  const renderSpinnerModal = () => <SpinnerModal open={mutations.some((m) => m.isLoading)} />;

  useEffect(() => {
    const mutationWithError = mutations.find((m) => m.error);

    if (mutationWithError?.error) {
      const { error } = mutationWithError;
      confirm({
        title: formStrings.error,
        message:
          displayAxiosError(error) ??
          ((mutationWithError instanceof FormError
            ? error.getDisplayMessage()
            : (error as any)?.message) ||
            strings.unknownError),
      });
    }
  }, [mutations.some((m) => m.error)]);

  return {
    renderAlerts,
    renderSpinnerModal,
    renderAlertsAndSpinnerModal: () => (
      <>
        {renderSpinnerModal()}
        {renderAlerts()}
      </>
    ),
    renderAlertDialog,
  };
};
